import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { TimeIcon } from "@chakra-ui/icons";
import { Flex, VStack, Image, Text, IconButton, Box, Skeleton, } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import API from "../../shared-utils/api";
import { IMG_URL } from "../common/Constant";
import { QRCodeCanvas } from "qrcode.react";
import { FiMaximize2 } from "react-icons/fi";
import ProfileBookingQrFullscreenModal from "./ProfileBookingQrFullscreenModal";
const ProfileBookingQr = ({ coverCode, bookingCode, text, showBorder = true, isPastBooking = false, changingQr = false, eventId, lastUpdatedQrTime, dynamicQrTime, }) => {
    const shouldFlip = changingQr && !isPastBooking;
    const [flipped, setFlipped] = useState(!shouldFlip);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const qrFlipTimeoutRef = useRef(null);
    const timerIntervalRef = useRef(null);
    const isFetchingRef = useRef(false);
    const prevShouldFlipRef = useRef(shouldFlip);
    const [code, setCode] = useState(coverCode);
    const [loading, setLoading] = useState(false);
    const [localLastUpdated, setLocalLastUpdated] = useState(lastUpdatedQrTime);
    const [localDynamicTime, setLocalDynamicTime] = useState(dynamicQrTime);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [timerActive, setTimerActive] = useState(false);
    useEffect(() => {
        return () => {
            if (qrFlipTimeoutRef.current)
                clearTimeout(qrFlipTimeoutRef.current);
            if (timerIntervalRef.current)
                clearInterval(timerIntervalRef.current);
        };
    }, []);
    useEffect(() => {
        if (shouldFlip && !prevShouldFlipRef.current) {
            setFlipped(false);
            setLoading(false);
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
            if (qrFlipTimeoutRef.current) {
                clearTimeout(qrFlipTimeoutRef.current);
                qrFlipTimeoutRef.current = null;
            }
            setTimerActive(false);
            setTimeRemaining(0);
        }
        else if (!shouldFlip && prevShouldFlipRef.current) {
            setFlipped(true);
            setLoading(false);
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
            if (qrFlipTimeoutRef.current) {
                clearTimeout(qrFlipTimeoutRef.current);
                qrFlipTimeoutRef.current = null;
            }
            setTimerActive(false);
            setTimeRemaining(0);
        }
        prevShouldFlipRef.current = shouldFlip;
    }, [shouldFlip]);
    useEffect(() => {
        if (code && coverCode && code !== coverCode && !isFetchingRef.current) {
            if (timerIntervalRef.current)
                clearInterval(timerIntervalRef.current);
            if (qrFlipTimeoutRef.current)
                clearTimeout(qrFlipTimeoutRef.current);
            setTimerActive(false);
            setTimeRemaining(0);
            setFlipped(false);
        }
        setCode(coverCode);
        if (Array.isArray(lastUpdatedQrTime)) {
            setLocalLastUpdated(lastUpdatedQrTime);
        }
        if (dynamicQrTime) {
            setLocalDynamicTime(dynamicQrTime);
        }
    }, [coverCode, lastUpdatedQrTime, dynamicQrTime]);
    const parseDateArray = (arr) => {
        if (!Array.isArray(arr))
            return 0;
        const [year = 1970, month = 1, day = 1, hour = 0, minute = 0, second = 0] = arr;
        return Date.UTC(year, month - 1, day, hour, minute, second);
    };
    const calculateRemainingTime = (lastUpdated, dynamicTime) => {
        if (!Array.isArray(lastUpdated) || !dynamicTime)
            return 0;
        const lastUpdatedMs = parseDateArray(lastUpdated);
        const expirationMs = lastUpdatedMs + Number(dynamicTime) * 1000;
        const nowMs = Date.now();
        const remainingMs = expirationMs - nowMs;
        return Math.max(0, Math.floor(remainingMs / 1000));
    };
    const fetchLatestCoverCode = async () => {
        var _a, _b, _c;
        if (!eventId || !bookingCode)
            return null;
        try {
            const url = `api/v1/rbzo/wallet/updateCode?eventId=${eventId}&bookingCode=${bookingCode}`;
            const { data } = await API.get(url);
            if (data.status_code !== "OK")
                throw new Error(data.status_message);
            const nextCode = (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.code;
            const lastTime = (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.lastUpdatedAt;
            const dynamicTime = (_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.dynamicQrTime;
            if (nextCode) {
                setCode(nextCode);
            }
            if (lastTime) {
                setLocalLastUpdated(lastTime);
            }
            if (dynamicTime) {
                setLocalDynamicTime(dynamicTime);
            }
            return { lastTime, dynamicTime };
        }
        catch (err) {
            console.error("Failed to update cover code", err);
            return null;
        }
    };
    const startTimerCountdown = (durationInSeconds) => {
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
        }
        if (qrFlipTimeoutRef.current) {
            clearTimeout(qrFlipTimeoutRef.current);
            qrFlipTimeoutRef.current = null;
        }
        setTimerActive(true);
        setTimeRemaining(durationInSeconds);
        timerIntervalRef.current = setInterval(() => {
            setTimeRemaining((prev) => {
                const newTime = prev - 1;
                if (newTime <= 0) {
                    if (timerIntervalRef.current)
                        clearInterval(timerIntervalRef.current);
                    setTimerActive(false);
                    if (qrFlipTimeoutRef.current) {
                        clearTimeout(qrFlipTimeoutRef.current);
                        qrFlipTimeoutRef.current = null;
                    }
                    setFlipped(false);
                    fetchAndRefreshQR();
                    return 0;
                }
                return newTime;
            });
        }, 1000);
    };
    const fetchAndRefreshQR = async () => {
        isFetchingRef.current = true;
        await fetchLatestCoverCode();
        setTimeout(() => {
            isFetchingRef.current = false;
        }, 100);
    };
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };
    const revealQRCode = async () => {
        if (!shouldFlip)
            return;
        if (loading)
            return;
        if (flipped)
            return;
        setFlipped(true);
        setLoading(true);
        isFetchingRef.current = true;
        const updated = await fetchLatestCoverCode();
        setLoading(false);
        setTimeout(() => {
            isFetchingRef.current = false;
        }, 100);
        const qrDuration = (updated === null || updated === void 0 ? void 0 : updated.dynamicTime) || localDynamicTime || 30;
        const timerDuration = Number(qrDuration);
        if (!timerActive && timerDuration > 0) {
            startTimerCountdown(timerDuration);
        }
        const cardVisibleDuration = Math.min(30000, timerDuration * 1000);
        if (qrFlipTimeoutRef.current)
            clearTimeout(qrFlipTimeoutRef.current);
        qrFlipTimeoutRef.current = setTimeout(() => {
            setFlipped(false);
            qrFlipTimeoutRef.current = null;
        }, cardVisibleDuration);
    };
    const openFullscreen = () => {
        if (shouldFlip && !flipped) {
            revealQRCode();
        }
        setIsFullscreen(true);
    };
    const closeFullscreen = () => {
        setIsFullscreen(false);
    };
    useEffect(() => {
        if (timeRemaining === 0 &&
            timerActive === false &&
            isFullscreen &&
            shouldFlip) {
            closeFullscreen();
        }
    }, [timeRemaining, timerActive, isFullscreen, shouldFlip]);
    return (_jsxs(_Fragment, { children: [_jsxs(VStack, { background: "#212121", my: 0, p: 4, pb: 6, border: showBorder ? "1px solid #272727" : "none", borderRadius: "8px", spacing: shouldFlip ? 3 : 4, position: "relative", children: [_jsx(Text, { color: "white", textAlign: "center", mt: shouldFlip ? 2 : 1, mb: shouldFlip ? 2 : 1, px: 2, lineHeight: shouldFlip ? "normal" : "20px", children: text }), shouldFlip ? (_jsx(VStack, { spacing: 3, width: "100%", children: _jsx(Box, { width: "100%", maxW: "220px", onClick: shouldFlip && !flipped && !loading ? revealQRCode : undefined, cursor: shouldFlip && !flipped && !loading ? "pointer" : "default", style: { perspective: "1000px" }, mt: 4, children: _jsxs(Box, { position: "relative", width: "100%", height: "250px", style: {
                                    transformStyle: "preserve-3d",
                                    transition: "transform 0.8s ease-in-out",
                                    transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                                }, children: [_jsxs(Box, { position: "absolute", width: "100%", height: "100%", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", flexDir: "column", gap: 2, style: {
                                            backfaceVisibility: "hidden",
                                            zIndex: flipped ? 0 : 1,
                                        }, children: [_jsx(Image, { src: `${IMG_URL}/icons/qr.png`, boxSize: 44 }), _jsx(Image, { src: `${IMG_URL}/icons/flipArrow.png`, h: "20px", w: "24px" }), _jsx(Text, { fontSize: "12px", fontWeight: "400", color: "#A383FF", textAlign: "center", px: 2, children: "Flip the card to scan the QR Code" })] }), _jsx(Box, { position: "absolute", width: "100%", height: "100%", borderRadius: "8px", display: "flex", flexDir: "column", gap: 2, alignItems: "center", justifyContent: "end", style: {
                                            transform: "rotateY(180deg)",
                                            backfaceVisibility: "hidden",
                                            zIndex: flipped ? 1 : 0,
                                        }, children: loading ? (_jsxs(VStack, { spacing: 4, children: [_jsx(Skeleton, { height: "220px", width: "220px", borderRadius: "8px", startColor: "gray.800", endColor: "gray.700", speed: 1.5 }), _jsx(Skeleton, { height: "40px", width: "120px", borderRadius: "4px", startColor: "gray.800", endColor: "gray.700" })] })) : (_jsxs(_Fragment, { children: [_jsx(Box, { bg: "white", p: 2, borderRadius: "8px", w: "100%", maxW: "220px", mx: "auto", children: _jsx(QRCodeCanvas, { value: code, size: 180, bgColor: "#FFFFFF", fgColor: "#161616", style: {
                                                            width: "100%",
                                                            height: "auto",
                                                            maxWidth: "100%",
                                                            display: "block",
                                                            background: "#FFFFFF",
                                                        } }) }), _jsxs(Flex, { alignItems: "center", justifyContent: "center", w: "100%", maxW: "220px", gap: 2, position: "relative", children: [_jsx(Flex, { flex: "1", px: 10, py: 2, border: "1px solid #363636", borderRadius: 4, alignItems: "center", justifyContent: "center", bgColor: "#1E1E1E", children: _jsx(Text, { color: "white", fontWeight: 600, fontSize: "16px", letterSpacing: 3, children: code }) }), _jsx(IconButton, { icon: _jsx(FiMaximize2, { size: 20 }), "aria-label": "Expand QR", onClick: openFullscreen, bg: "transparent", size: "md", minW: "auto", h: "auto", _hover: {
                                                                transform: "scale(1.1)",
                                                                transition: "all 0.2s ease-in-out",
                                                                color: "purple.400",
                                                            }, _active: {
                                                                transform: "scale(0.95)",
                                                            }, color: "white", zIndex: 10 })] })] })) })] }) }) })) : (_jsxs(_Fragment, { children: [_jsx(Box, { position: "relative", width: "100%", maxW: "255px", borderRadius: "8px", children: _jsx(Box, { bg: "white", p: 2, borderRadius: "8px", w: "100%", maxW: "250px", mx: "auto", children: _jsx(QRCodeCanvas, { value: code, size: 200, bgColor: "#FFFFFF", fgColor: "#161616", style: {
                                            width: "100%",
                                            height: "auto",
                                            maxWidth: "100%",
                                            display: "block",
                                            background: "#FFFFFF",
                                        } }) }) }), _jsxs(Flex, { alignItems: "center", justifyContent: "center", w: "100%", maxW: "250px", gap: 2, position: "relative", children: [_jsx(Flex, { flex: "1", px: 10, py: 2, border: "1px solid #363636", borderRadius: 4, alignItems: "center", justifyContent: "center", bgColor: "#1E1E1E", children: _jsx(Text, { color: "white", fontWeight: 600, fontSize: "16px", letterSpacing: 3, children: code }) }), _jsx(IconButton, { icon: _jsx(FiMaximize2, { size: 20 }), "aria-label": "Expand QR", onClick: openFullscreen, bg: "transparent", size: "md", minW: "auto", h: "auto", _hover: {
                                            transform: "scale(1.1)",
                                            transition: "all 0.2s ease-in-out",
                                            color: "purple.400",
                                        }, _active: {
                                            transform: "scale(0.95)",
                                        }, color: "white", zIndex: 10 })] })] })), shouldFlip && timerActive && timeRemaining > 0 && (_jsxs(Flex, { alignItems: "center", justifyContent: "center", mt: 2, gap: 2, children: [_jsx(TimeIcon, { color: "#A383FF", boxSize: 4 }), _jsx(Text, { color: "#A383FF", fontWeight: 600, fontSize: "14px", children: formatTime(timeRemaining) })] }))] }), _jsx(ProfileBookingQrFullscreenModal, { isOpen: isFullscreen, onClose: closeFullscreen, code: code, shouldFlip: shouldFlip, timerActive: timerActive, timeRemaining: timeRemaining, formatTime: formatTime })] }));
};
export default ProfileBookingQr;
